/* eslint-disable */
"use client";

import { useRouter } from "./router";
import { useState, useTransition, type ReactNode } from "react";
import {
  BadgePercent,
  CalendarRange,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  Landmark,
  Loader2,
  Plus,
  Rocket,
  Save,
  Trash2,
} from "lucide-react";
import type { payrollPresets, sssBrackets, taxBrackets } from "./types";
import { Button, Card, Modal, Pill } from "./ui";
import { PageHeader } from "./sidebar";
import {
  applyPayrollPresets,
  applySssBrackets,
  applyTaxBrackets,
  savePayrollPresets,
  saveSssBrackets,
  saveTaxBrackets,
  type PresetRowInput,
  type SssBracketRow,
  type TaxBracketRow,
} from "./api";
import { DEFAULT_PRESETS, MONTH_OFFSET_LABELS, resolvePreset } from "./payroll-shared";
import { cn, formatDate, formatPeso } from "./utils";

type SssRow = typeof sssBrackets.$inferSelect;
type TaxRow = typeof taxBrackets.$inferSelect;
type PresetRow = typeof payrollPresets.$inferSelect;

/* ---------------------------------- diffing ---------------------------------- */
export type Change = { scope: string; field: string; from: string; to: string };

function diffLists<T>(
  live: T[],
  next: T[],
  scopeName: (row: T, i: number) => string,
  fields: Array<{ key: string; get: (row: T) => string }>
): Change[] {
  const changes: Change[] = [];
  const max = Math.max(live.length, next.length);
  for (let i = 0; i < max; i++) {
    const a = live[i];
    const b = next[i];
    if (a && !b) {
      changes.push({
        scope: scopeName(a, i),
        field: "Entry",
        from: fields.map((f) => f.get(a)).join(" \u00b7 "),
        to: "Removed",
      });
      continue;
    }
    if (!a && b) {
      changes.push({
        scope: scopeName(b, i),
        field: "Entry",
        from: "None",
        to: fields.map((f) => f.get(b)).join(" \u00b7 "),
      });
      continue;
    }
    if (!a || !b) continue;
    for (const f of fields) {
      const from = f.get(a);
      const to = f.get(b);
      if (from !== to) changes.push({ scope: scopeName(b, i), field: f.key, from, to });
    }
  }
  return changes;
}

/* ------------------------------ confirm modal -------------------------------- */
function ApplyConfirmModal({
  title,
  changes,
  pending,
  onCancel,
  onProceed,
}: {
  title: string;
  changes: Change[];
  pending: boolean;
  onCancel: () => void;
  onProceed: () => void;
}) {
  return (
    <Modal
      open
      onClose={pending ? () => {} : onCancel}
      title={`Apply ${title}?`}
      subtitle="These changes will be used by the payroll engine the next time you generate a payroll. Previously finalized payroll runs are never recomputed."
      icon={<Rocket className="h-5 w-5" />}
      wide
    >
      {changes.length === 0 ? (
        <div className="rounded-xl bg-surface2 px-4 py-5 text-center text-sm text-muted">
          Nothing to apply &mdash; the saved values already match what payroll is using.
        </div>
      ) : (
        <>
          <p className="mb-3 text-xs font-bold tracking-wide text-faint uppercase">
            {changes.length} change{changes.length === 1 ? "" : "s"} to apply
          </p>
          <div className="max-h-[46vh] space-y-1.5 overflow-y-auto pr-1">
            {changes.map((c, i) => (
              <div key={i} className="rounded-xl bg-surface2 px-3.5 py-2.5">
                <p className="text-[11px] font-bold tracking-wide text-primary-strong uppercase">
                  {c.scope} &#183; {c.field}
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm font-semibold">
                  <span className="rounded-lg bg-[color-mix(in_oklab,var(--danger)_12%,transparent)] px-2.5 py-1 text-danger line-through decoration-1">
                    {c.from}
                  </span>
                  <span className="text-faint">&rarr;</span>
                  <span className="rounded-lg bg-[color-mix(in_oklab,var(--success)_14%,transparent)] px-2.5 py-1 text-success">
                    {c.to}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-6 flex justify-end gap-2.5">
        <Button variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button variant="accent" onClick={onProceed} disabled={pending || changes.length === 0}>
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
          Proceed &amp; apply
        </Button>
      </div>
    </Modal>
  );
}

/* --------------------------------- section UI --------------------------------- */
function SectionHeader({
  icon,
  tone,
  title,
  description,
  dirty,
  savedFlash,
  appliedFlash,
  pending,
  onAdd,
  addLabel,
  onSave,
  onApply,
}: {
  icon: ReactNode;
  tone: "blue" | "yellow";
  title: string;
  description: string;
  dirty: boolean;
  savedFlash: boolean;
  appliedFlash: boolean;
  pending: boolean;
  onAdd: () => void;
  addLabel: string;
  onSave: () => void;
  onApply: () => void;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 sm:px-6">
      <div className="flex items-start gap-3.5">
        <span
          className={cn(
            "flex h-10 w-10 items-center justify-center rounded-xl",
            tone === "yellow"
              ? "bg-[color-mix(in_oklab,var(--accent)_15%,transparent)] text-accent"
              : "bg-[color-mix(in_oklab,var(--primary)_14%,transparent)] text-primary-strong"
          )}
        >
          {icon}
        </span>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-base font-bold text-text">{title}</h2>
            {dirty ? (
              <Pill tone="yellow">
                <CircleAlert className="h-3 w-3" /> Not applied to payroll
              </Pill>
            ) : (
              <Pill tone="green">
                <CheckCircle2 className="h-3 w-3" /> Live in payroll
              </Pill>
            )}
          </div>
          <p className="mt-0.5 max-w-lg text-xs leading-relaxed text-muted">{description}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        {savedFlash && (
          <span className="fade-in inline-flex items-center gap-1.5 text-xs font-bold text-info">
            <CheckCircle2 className="h-4 w-4" /> Saved
          </span>
        )}
        {appliedFlash && (
          <span className="fade-in inline-flex items-center gap-1.5 text-xs font-bold text-success">
            <CheckCircle2 className="h-4 w-4" /> Applied
          </span>
        )}
        <Button variant="outline" size="sm" onClick={onAdd} disabled={pending}>
          <Plus className="h-3.5 w-3.5" /> {addLabel}
        </Button>
        <Button variant="outline" size="sm" onClick={onSave} disabled={pending}>
          {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save changes
        </Button>
        <Button variant="accent" size="sm" onClick={onApply} disabled={pending}>
          <Rocket className="h-3.5 w-3.5" /> Apply
        </Button>
      </div>
    </div>
  );
}

function ErrorNote({ error }: { error: string }) {
  if (!error) return null;
  return (
    <p className="mt-4 rounded-xl border border-[color-mix(in_oklab,var(--danger)_35%,transparent)] bg-[color-mix(in_oklab,var(--danger)_10%,transparent)] px-3.5 py-2.5 text-xs font-semibold text-danger">
      {error}
    </p>
  );
}

const numCell =
  "h-9 w-full rounded-lg border border-border bg-surface2 px-2.5 text-right text-xs font-semibold text-text outline-none transition-all focus:border-primary focus:ring-2 focus:ring-[color-mix(in_oklab,var(--primary)_18%,transparent)]";
const cellSelect =
  "h-9 w-full cursor-pointer rounded-lg border border-border bg-surface2 px-2.5 text-xs font-semibold text-text outline-none transition-all focus:border-primary focus:ring-2 focus:ring-[color-mix(in_oklab,var(--primary)_18%,transparent)]";
const cellInput =
  "h-9 w-full rounded-lg border border-border bg-surface2 px-2.5 text-xs font-semibold text-text outline-none transition-all placeholder:text-faint focus:border-primary focus:ring-2 focus:ring-[color-mix(in_oklab,var(--primary)_18%,transparent)]";

const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1);

/* ----------------------------- quick range editor ----------------------------- */
function toPresetInput(r: PresetRow): PresetRowInput {
  return {
    label: r.label,
    mode: r.mode,
    startDay: String(r.startDay),
    endDay: String(r.endDay),
    monthOffset: String(r.monthOffset),
    startDate: r.startDate ?? "",
    endDate: r.endDate ?? "",
    startMonthOffset: String(r.startMonthOffset ?? 0),
  };
}

function QuickRangeEditor({ draft, live }: { draft: PresetRow[]; live: PresetRow[] }) {
  const router = useRouter();
  const initialRows = draft.map(toPresetInput);

  // Ensure "First Half" and "Second Half" are always at the top
  const ensurePrimary = (rs: PresetRowInput[]) => {
    const primaryLabels = ["First Half", "Second Half"];
    const out = [...rs];
    for (const label of primaryLabels) {
      if (!out.find((r) => r.label === label)) {
        const def = DEFAULT_PRESETS.find((p) => p.label === label);
        if (def) {
          out.unshift({
            label: def.label,
            mode: def.mode,
            startDay: String(def.startDay),
            endDay: String(def.endDay),
            monthOffset: String(def.monthOffset),
            startDate: "",
            endDate: "",
            startMonthOffset: String(def.startMonthOffset ?? 0),
          });
        }
      }
    }
    return out;
  };

  const [rows, setRows] = useState<PresetRowInput[]>(ensurePrimary(initialRows));
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const [appliedFlash, setAppliedFlash] = useState(false);
  const [confirming, setConfirming] = useState<Change[] | null>(null);
  const [pending, startTransition] = useTransition();

  const liveRows = live.map(toPresetInput);
  const describe = (r: PresetRowInput) =>
    r.mode === "fixed"
      ? `${r.startDate || "\u2014"} \u2192 ${r.endDate || "\u2014"}`
      : `day ${r.startDay} \u2192 ${r.endDay === "0" ? "end of month" : `day ${r.endDay}`}`;

  const changes = diffLists<PresetRowInput>(
    liveRows,
    rows,
    (r, i) => `Preset ${i + 1}${r.label ? ` (${r.label})` : ""}`,
    [
      { key: "Label", get: (r) => r.label || "\u2014" },
      { key: "Mode", get: (r) => (r.mode === "fixed" ? "Fixed dates" : "Day of month") },
      { key: "Range", get: describe },
      {
        key: "Month",
        get: (r) => (r.mode === "fixed" ? "\u2014" : MONTH_OFFSET_LABELS[Number(r.monthOffset)] ?? "This month"),
      },
    ]
  );

  const set = (i: number, key: keyof PresetRowInput, value: string) => {
    setSavedFlash(false);
    setAppliedFlash(false);
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  };

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    setSavedFlash(false);
    setAppliedFlash(false);
    setRows((rs) => {
      const copy = [...rs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  const preview = (r: PresetRowInput) => {
    const resolved = resolvePreset({
      label: r.label,
      mode: r.mode,
      startDay: Number(r.startDay) || 1,
      startMonthOffset: Number(r.startMonthOffset) || 0,
      endDay: Number(r.endDay),
      monthOffset: Number(r.monthOffset) || 0,
      startDate: r.startDate || null,
      endDate: r.endDate || null,
    });
    return resolved ? `${formatDate(resolved.start)} \u2014 ${formatDate(resolved.end)}` : "Incomplete";
  };

  const doSave = () => {
    setError("");
    setAppliedFlash(false);
    startTransition(() => {
      void (async () => {
        const res = await savePayrollPresets(rows);
        if (!res.ok) return setError(res.error);
        setSavedFlash(true);
        router.refresh();
      })();
    });
  };

  const doApply = () => {
    setError("");
    startTransition(() => {
      void (async () => {
        const res = await applyPayrollPresets(rows);
        if (!res.ok) {
          setConfirming(null);
          return setError(res.error);
        }
        setConfirming(null);
        setSavedFlash(false);
        setAppliedFlash(true);
        router.refresh();
      })();
    });
  };

  return (
    <Card className="rise-in overflow-hidden">
      <SectionHeader
        icon={<CalendarRange className="h-5 w-5" />}
        tone="yellow"
        title="Quick range presets"
        description="Customize the one-click date buttons in the \u201cGenerate payroll\u201d screen. Use a recurring day-of-month cutoff, or pin an exact calendar range."
        dirty={changes.length > 0}
        savedFlash={savedFlash}
        appliedFlash={appliedFlash}
        pending={pending}
        onAdd={() =>
          setRows((rs) => [
            ...rs,
            { label: "", mode: "monthly", startDay: "1", endDay: "15", monthOffset: "0", startDate: "", endDate: "" },
          ])
        }
        addLabel="Add preset"
        onSave={doSave}
        onApply={() => setConfirming(changes)}
      />

      <div className="mt-4 space-y-2.5 px-5 pb-5 sm:px-6">
        {rows.length === 0 && (
          <div className="rounded-xl border-2 border-dashed border-border/60 p-2">
            <div className="rounded-xl border border-border bg-surface2/40 px-4 py-6 text-center text-xs text-faint">
              No presets configured &mdash; the payroll screen falls back to the built-in &ldquo;1st &ndash; 15th&rdquo;, &ldquo;16th &ndash; end&rdquo;, and &ldquo;Full month&rdquo; buttons.
            </div>
          </div>
        )}

        {rows.map((r, i) => {
          const isPrimary = r.label === "First Half" || r.label === "Second Half";
          return (
            <div
              key={i}
              className={cn(
                "rounded-xl border p-3 transition-all",
                isPrimary
                  ? "border-accent/40 bg-surface2 shadow-[0_4px_20px_-12px_var(--accent)]"
                  : "border-border/80 bg-surface2/60"
              )}
            >
              {isPrimary && (
                <div className="mb-2 flex items-center gap-1.5 px-0.5">
                  <BadgePercent className="h-3.5 w-3.5 text-accent-strong" />
                  <span className="text-[10px] font-bold tracking-[0.12em] text-accent-strong uppercase">
                    Primary range configuration
                  </span>
                </div>
              )}
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-[1.4fr_130px_1fr_auto]">
                <input
                  className={cellInput}
                  value={r.label}
                  onChange={(e) => set(i, "label", e.target.value)}
                  placeholder="Button label (e.g. First Half)"
                  readOnly={isPrimary}
                />
                <select className={cellSelect} value={r.mode} onChange={(e) => set(i, "mode", e.target.value)}>
                  <option value="monthly">Day of month</option>
                  <option value="fixed">Fixed dates</option>
                </select>

                {r.mode === "monthly" ? (
                  <div className="grid grid-cols-3 gap-2">
                    <select
                      className={cellSelect}
                      value={r.startMonthOffset === "-1" ? "prev" : r.startDay}
                      onChange={(e) => {
                        if (e.target.value === "prev") {
                          set(i, "startDay", "24");
                          set(i, "startMonthOffset", "-1");
                        } else {
                          set(i, "startDay", e.target.value);
                          set(i, "startMonthOffset", "0");
                        }
                      }}
                    >
                      <option value="prev">Previous Month (24th)</option>
                      {DAY_OPTIONS.map((d) => (
                        <option key={d} value={d}>
                          From day {d}
                        </option>
                      ))}
                    </select>
                    <select className={cellSelect} value={r.endDay} onChange={(e) => set(i, "endDay", e.target.value)}>
                      <option value="0">To end of month</option>
                      {DAY_OPTIONS.map((d) => (
                        <option key={d} value={d}>
                          To day {d}
                        </option>
                      ))}
                    </select>
                    <select
                      className={cellSelect}
                      value={r.monthOffset}
                      onChange={(e) => set(i, "monthOffset", e.target.value)}
                    >
                      {[0, -1, -2, 1].map((o) => (
                        <option key={o} value={o}>
                          {MONTH_OFFSET_LABELS[o]}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      className={cellInput}
                      value={r.startDate}
                      onChange={(e) => set(i, "startDate", e.target.value)}
                    />
                    <input
                      type="date"
                      className={cellInput}
                      value={r.endDate}
                      onChange={(e) => set(i, "endDate", e.target.value)}
                    />
                  </div>
                )}

                <div className="flex items-center justify-end gap-1">
                  {!isPrimary && (
                    <>
                      <button
                        type="button"
                        onClick={() => move(i, -1)}
                        disabled={i === 0 || rows[i - 1].label === "First Half" || rows[i - 1].label === "Second Half"}
                        className="flex h-8 w-7 cursor-pointer items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface3 hover:text-text disabled:pointer-events-none disabled:opacity-30"
                        title="Move up"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(i, 1)}
                        disabled={i === rows.length - 1}
                        className="flex h-8 w-7 cursor-pointer items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface3 hover:text-text disabled:pointer-events-none disabled:opacity-30"
                        title="Move down"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSavedFlash(false);
                          setAppliedFlash(false);
                          setRows((rs) => rs.filter((_, idx) => idx !== i));
                        }}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface3 hover:text-danger"
                        title="Remove preset"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <p className="mt-2 flex items-center gap-1.5 pl-0.5 text-[11px] font-semibold text-faint">
                <CalendarRange className="h-3.5 w-3.5" />
                Resolves to <span className="text-accent-strong">{preview(r)}</span>
                {r.mode === "monthly" && <span className="text-faint">&#183; recalculated every run</span>}
                {isPrimary && (
                  <span className="ml-auto rounded-full bg-accent/15 px-2 py-0.5 text-[10px] text-accent-strong">
                    Default range
                  </span>
                )}
              </p>
            </div>
          );
        })}

        <ErrorNote error={error} />
      </div>

      {confirming && (
        <ApplyConfirmModal
          title="quick range presets"
          changes={confirming}
          pending={pending}
          onCancel={() => setConfirming(null)}
          onProceed={doApply}
        />
      )}
    </Card>
  );
}

/* -------------------------------- sss editor --------------------------------- */
function SssEditor({ draft, live }: { draft: SssRow[]; live: SssRow[] }) {
  const router = useRouter();
  const toInput = (r: SssRow): SssBracketRow => ({
    salaryFrom: String(Number(r.salaryFrom)),
    salaryTo: String(Number(r.salaryTo)),
    employeeShare: String(Number(r.employeeShare)),
  });
  const [rows, setRows] = useState<SssBracketRow[]>(draft.map(toInput));
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const [appliedFlash, setAppliedFlash] = useState(false);
  const [confirming, setConfirming] = useState<Change[] | null>(null);
  const [pending, startTransition] = useTransition();

  const money = (v: string) => (v === "" || isNaN(Number(v)) ? "\u2014" : formatPeso(Number(v)));
  const changes = diffLists<SssBracketRow>(
    live.map(toInput),
    rows,
    (r, i) => `Bracket ${i + 1}`,
    [
      { key: "Salary from", get: (r) => money(r.salaryFrom) },
      { key: "Salary to", get: (r) => money(r.salaryTo) },
      { key: "Employee share", get: (r) => money(r.employeeShare) },
    ]
  );

  const set = (i: number, key: keyof SssBracketRow, value: string) => {
    setSavedFlash(false);
    setAppliedFlash(false);
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  };

  const doSave = () => {
    setError("");
    setAppliedFlash(false);
    startTransition(() => {
      void (async () => {
        const res = await saveSssBrackets(rows);
        if (!res.ok) return setError(res.error);
        setSavedFlash(true);
        router.refresh();
      })();
    });
  };

  const doApply = () => {
    setError("");
    startTransition(() => {
      void (async () => {
        const res = await applySssBrackets(rows);
        if (!res.ok) {
          setConfirming(null);
          return setError(res.error);
        }
        setConfirming(null);
        setSavedFlash(false);
        setAppliedFlash(true);
        router.refresh();
      })();
    });
  };

  return (
    <Card className="rise-in overflow-hidden">
      <SectionHeader
        icon={<Landmark className="h-5 w-5" />}
        tone="yellow"
        title="SSS salary brackets"
        description="Monthly salary credit ranges and the employee share deducted. Semi-monthly payrolls withhold half."
        dirty={changes.length > 0}
        savedFlash={savedFlash}
        appliedFlash={appliedFlash}
        pending={pending}
        onAdd={() =>
          setRows((rs) => [
            ...rs,
            { salaryFrom: rs.length ? String(Number(rs[rs.length - 1].salaryTo) + 0.01) : "0", salaryTo: "", employeeShare: "" },
          ])
        }
        addLabel="Add bracket"
        onSave={doSave}
        onApply={() => setConfirming(changes)}
      />
      <div className="mt-4 px-5 pb-5 sm:px-6">
        <div className="overflow-x-auto rounded-xl border border-border/70 bg-surface2/30">
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="text-left text-[10px] font-bold tracking-[0.12em] text-faint uppercase">
                <th className="py-2 pr-3 pl-3 sm:pl-4">Monthly salary from (&#8369;)</th>
                <th className="py-2 pr-3">Monthly salary to (&#8369;)</th>
                <th className="py-2 pr-3">Employee share (&#8369;)</th>
                <th className="w-10 pr-3 sm:pr-4" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-border/50">
                  <td className="py-1.5 pr-3 pl-3 sm:pl-4"><input className={numCell} inputMode="decimal" value={r.salaryFrom} onChange={(e) => set(i, "salaryFrom", e.target.value)} /></td>
                  <td className="py-1.5 pr-3"><input className={numCell} inputMode="decimal" value={r.salaryTo} onChange={(e) => set(i, "salaryTo", e.target.value)} placeholder="e.g. 99999999" /></td>
                  <td className="py-1.5 pr-3"><input className={numCell} inputMode="decimal" value={r.employeeShare} onChange={(e) => set(i, "employeeShare", e.target.value)} /></td>
                  <td className="py-1.5 pr-3 sm:pr-4">
                    <button
                      type="button"
                      onClick={() => { setSavedFlash(false); setAppliedFlash(false); setRows((rs) => rs.filter((_, idx) => idx !== i)); }}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface3 hover:text-danger"
                      title="Remove bracket"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ErrorNote error={error} />
      </div>

      {confirming && (
        <ApplyConfirmModal
          title="SSS salary brackets"
          changes={confirming}
          pending={pending}
          onCancel={() => setConfirming(null)}
          onProceed={doApply}
        />
      )}
    </Card>
  );
}

/* -------------------------------- tax editor --------------------------------- */
function TaxEditor({ draft, live }: { draft: TaxRow[]; live: TaxRow[] }) {
  const router = useRouter();
  const toInput = (r: TaxRow): TaxBracketRow => ({
    salaryFrom: String(Number(r.salaryFrom)),
    salaryTo: String(Number(r.salaryTo)),
    baseTax: String(Number(r.baseTax)),
    rateOver: String(Number(r.rateOver) * 100),
  });
  const [rows, setRows] = useState<TaxBracketRow[]>(draft.map(toInput));
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const [appliedFlash, setAppliedFlash] = useState(false);
  const [confirming, setConfirming] = useState<Change[] | null>(null);
  const [pending, startTransition] = useTransition();

  const money = (v: string) => (v === "" || isNaN(Number(v)) ? "\u2014" : formatPeso(Number(v)));
  const changes = diffLists<TaxBracketRow>(
    live.map(toInput),
    rows,
    (r, i) => `Bracket ${i + 1}`,
    [
      { key: "Salary from", get: (r) => money(r.salaryFrom) },
      { key: "Salary to", get: (r) => money(r.salaryTo) },
      { key: "Base tax", get: (r) => money(r.baseTax) },
      { key: "Rate over floor", get: (r) => (r.rateOver === "" ? "\u2014" : `${Number(r.rateOver)}%`) },
    ]
  );

  const set = (i: number, key: keyof TaxBracketRow, value: string) => {
    setSavedFlash(false);
    setAppliedFlash(false);
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  };

  const doSave = () => {
    setError("");
    setAppliedFlash(false);
    startTransition(() => {
      void (async () => {
        const res = await saveTaxBrackets(rows);
        if (!res.ok) return setError(res.error);
        setSavedFlash(true);
        router.refresh();
      })();
    });
  };

  const doApply = () => {
    setError("");
    startTransition(() => {
      void (async () => {
        const res = await applyTaxBrackets(rows);
        if (!res.ok) {
          setConfirming(null);
          return setError(res.error);
        }
        setConfirming(null);
        setSavedFlash(false);
        setAppliedFlash(true);
        router.refresh();
      })();
    });
  };

  return (
    <Card className="rise-in overflow-hidden">
      <SectionHeader
        icon={<BadgePercent className="h-5 w-5" />}
        tone="blue"
        title="Tax salary brackets"
        description="Monthly withholding tax = base tax + (rate \u00d7 salary over the bracket floor). Minimum-wage earners flagged during enrollment are always exempt."
        dirty={changes.length > 0}
        savedFlash={savedFlash}
        appliedFlash={appliedFlash}
        pending={pending}
        onAdd={() =>
          setRows((rs) => [
            ...rs,
            { salaryFrom: rs.length ? String(Number(rs[rs.length - 1].salaryTo) + 0.01) : "0", salaryTo: "", baseTax: "0", rateOver: "0" },
          ])
        }
        addLabel="Add bracket"
        onSave={doSave}
        onApply={() => setConfirming(changes)}
      />
      <div className="mt-4 px-5 pb-5 sm:px-6">
        <div className="overflow-x-auto rounded-xl border border-border/70 bg-surface2/30">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="text-left text-[10px] font-bold tracking-[0.12em] text-faint uppercase">
                <th className="py-2 pr-3 pl-3 sm:pl-4">Monthly salary from (&#8369;)</th>
                <th className="py-2 pr-3">Monthly salary to (&#8369;)</th>
                <th className="py-2 pr-3">Base tax (&#8369;)</th>
                <th className="py-2 pr-3">Rate over floor (%)</th>
                <th className="w-10 pr-3 sm:pr-4" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-border/50">
                  <td className="py-1.5 pr-3 pl-3 sm:pl-4"><input className={numCell} inputMode="decimal" value={r.salaryFrom} onChange={(e) => set(i, "salaryFrom", e.target.value)} /></td>
                  <td className="py-1.5 pr-3"><input className={numCell} inputMode="decimal" value={r.salaryTo} onChange={(e) => set(i, "salaryTo", e.target.value)} /></td>
                  <td className="py-1.5 pr-3"><input className={numCell} inputMode="decimal" value={r.baseTax} onChange={(e) => set(i, "baseTax", e.target.value)} /></td>
                  <td className="py-1.5 pr-3"><input className={numCell} inputMode="decimal" value={r.rateOver} onChange={(e) => set(i, "rateOver", e.target.value)} /></td>
                  <td className="py-1.5 pr-3 sm:pr-4">
                    <button
                      type="button"
                      onClick={() => { setSavedFlash(false); setAppliedFlash(false); setRows((rs) => rs.filter((_, idx) => idx !== i)); }}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface3 hover:text-danger"
                      title="Remove bracket"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-faint">
          Example: 33,333.01&ndash;66,667 with base {formatPeso(1875)} and 20% &rarr; a &#8369;50,000 salary pays {formatPeso(1875 + (50000 - 33333.01) * 0.2)}.
        </p>
        <ErrorNote error={error} />
      </div>

      {confirming && (
        <ApplyConfirmModal
          title="tax salary brackets"
          changes={confirming}
          pending={pending}
          onCancel={() => setConfirming(null)}
          onProceed={doApply}
        />
      )}
    </Card>
  );
}

/* ------------------------------------ page ----------------------------------- */
export function PreferencesClient({
  sssDraft,
  sssLive,
  taxDraft,
  taxLive,
  presetDraft,
  presetLive,
}: {
  sssDraft: SssRow[];
  sssLive: SssRow[];
  taxDraft: TaxRow[];
  taxLive: TaxRow[];
  presetDraft: PresetRow[];
  presetLive: PresetRow[];
}) {
  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Preferences"
        description="Configure quick range presets and the statutory SSS & withholding-tax salary brackets. Save changes to keep your edits, then press Apply to push them into payroll computation."
        actions={<Pill tone="blue">Save keeps edits &#183; Apply affects payroll</Pill>}
      />
      <div className="space-y-4">
        <QuickRangeEditor draft={presetDraft} live={presetLive} />
        <SssEditor draft={sssDraft} live={sssLive} />
        <TaxEditor draft={taxDraft} live={taxLive} />
      </div>
    </>
  );
}
